/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useRef, useState, useCallback, useEffect } from 'react'
import {
    DEFAULT_BATTING_STATS,
    DEFAULT_PITCHING_STATS,
    DEFAULT_CATCHING_STATS,
} from 'bam-node-baseball-savant/apps/utils/possible-comp-tool-stats.ts'
import { StatPickerProps, StatGroup, PlayerRow } from './utils/types.ts'
import { prettyStatNames, groupStatSelects } from './utils/stat-groupings.ts'
import StatPickerGroup from './StatPickerGroup.tsx'
import StatPickerDraggable from './StatPickerDraggable.tsx'
import { isStatsFull } from './utils/helpers.ts'
import { useDimensions } from './utils/responsive-dimensions.tsx'

const PITCH_NAMES: Record<string, string> = {
    ff: '4-Seam Fastball',
    si: 'Sinker',
    fc: 'Cutter',
    ch: 'Changeup',
    fs: 'Splitter',
    fo: 'Forkball',
    sc: 'Screwball',
    cu: 'Curveball',
    sl: 'Slider',
    st: 'Sweeper',
    sv: 'Slurve',
    kn: 'Knuckleball',
}

export default function StatPicker({
    statRef,
    stats,
    selected,
    currentType,
    showTitle,
    showTeams,
    selectedPlayers,
    onAdd,
}: StatPickerProps) {
    const [searchFilter, setSearchFilter] = useState('')
    const [draftSelected, setDraftSelected] = useState(selected)
    const [flashingStat, setFlashingStat] = useState<string | null>(null)
    const [expandedPitches, setExpandedPitches] = useState<string[]>([])

    const togglePitchDrawer = (pitch: string) => {
        setExpandedPitches((prev) =>
            prev.includes(pitch)
                ? prev.filter((p) => p !== pitch)
                : [...prev, pitch]
        )
    }

    // Force a re-render once the DOM is painted (allows for the g elements defining
    // drag-drop groups to actually exist)
    const [, setIsMounted] = useState(false)
    useEffect(() => {
        setIsMounted(true)
    }, [])

    useEffect(() => {
        setDraftSelected(selected)

        const activePitchPrefixes = new Set<string>()
        selected.forEach((stat) => {
            const prefix = (stat as string).split('_')[0]

            if (PITCH_NAMES[prefix]) {
                activePitchPrefixes.add(prefix)
            }
        })

        setExpandedPitches(Array.from(activePitchPrefixes))
    }, [selected])

    const handleApplyRef = useRef(() => onAdd(draftSelected))

    useEffect(() => {
        handleApplyRef.current = () => onAdd(draftSelected)
    })

    useEffect(() => {
        if (!statRef.current) {
            return
        }

        statRef.current.addEventListener('toggle', (e) => {
            if (e.newState !== 'closed') {
                return
            }

            handleApplyRef.current()
        })
    }, [statRef])

    const dimensions = useDimensions()
    const { isMobile } = dimensions

    const isFull = isStatsFull(
        draftSelected,
        currentType,
        showTitle,
        showTeams,
        false,
        dimensions
    )

    const filteredStats = stats.filter((d) => {
        if (searchFilter === '') {
            return true
        }

        if (!prettyStatNames[d]) {
            return true
        }

        return prettyStatNames[d]
            .toLowerCase()
            .includes(searchFilter.toLowerCase())
    })

    const handleToggleStat = (stat: string) => {
        const isSelected = draftSelected.includes(stat as keyof PlayerRow)

        // If it's not already selected and the picker is full, don't allow adding.
        if (!isSelected && isFull) {
            return
        }

        if (isSelected) {
            setDraftSelected((prev) => prev.filter((s) => s !== stat))
        } else {
            setFlashingStat(stat)
            setTimeout(() => setFlashingStat(null), 150)
            setDraftSelected((prev) => [...prev, stat as keyof PlayerRow])
        }
    }

    const groupedStats = groupStatSelects(filteredStats, currentType)

    const handleSelectDefaults = useCallback(
        (defaultStats: string[]) => {
            const availableStats = stats as string[]
            const applicableDefaults = defaultStats.filter((stat) =>
                availableStats.includes(stat)
            )
            setDraftSelected(applicableDefaults as (keyof PlayerRow)[])
        },
        [stats]
    )

    const groupedDraft = groupStatSelects(
        draftSelected as (keyof PlayerRow)[],
        currentType
    )
    const existingDraftTypes = (
        Object.keys(groupedDraft) as StatGroup[]
    ).filter((type) => groupedDraft[type].length > 0)

    const renderStatNode = (stat: string) => {
        const isSelected = draftSelected.includes(stat as keyof PlayerRow)
        const isFlashing = flashingStat === stat

        let backgroundColor = 'white'
        if (isSelected) backgroundColor = '#e6e6e6'
        if (isFlashing) backgroundColor = 'rgba(57, 144, 152, 0.4)'

        return (
            <div
                key={stat}
                onClick={() => handleToggleStat(stat)}
                className="stat-picker-scroll-element"
                style={{
                    cursor: isSelected || isFull ? 'default' : 'pointer',
                    background: backgroundColor,
                    opacity: isSelected ? 0.6 : 1,
                }}
            >
                {stat === 'percent_rank_swing_take_run_value' &&
                currentType === 'pitcher'
                    ? 'Pitching'
                    : prettyStatNames[stat]}
            </div>
        )
    }

    return (
        <div
            {...({
                popover: 'auto',
            } as React.HTMLAttributes<HTMLDivElement>)}
            id="stat-search"
            ref={statRef}
            className="stat-picker-popover comp-tool-popover"
        >
            <style>{`
                ${
                    isMobile
                        ? `
                    .stat-picker-popover {
                        width: 95vw !important;
                        padding: 10px;
                    }
                    .stat-picker-overall-wrapper {
                        gap: 10px; 
                    }
                    .stat-picker-left-side-wrapper {
                        flex: 0 0 190px;
                    }
                    .stat-picker-right-side-wrapper {
                        flex: 1;
                        min-width: 0;
                    }
                    .stat-picker-button-wrapper {
                        flex-direction: column; 
                        gap: 2px;
                    }
                    .stat-picker-default-button {
                        font-size: 10px; 
                        padding: 4px;
                        margin-top: 0;
                    }
                    .stat-picker-scroll-element {
                        font-size: 10px;
                        padding: 6px 10px 4px; 
                        word-break: break-word;
                    }
                    #stat-picker-search-bar {
                        font-size: 10px;
                    }
                `
                        : ''
                }
            `}</style>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                }}
            >
                <h3 style={{ margin: '5px' }}>Choose & Order Stats</h3>
                <button
                    type="button"
                    onClick={() => {
                        const popover = document.getElementById('stat-search')
                        if (popover && 'hidePopover' in popover) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ;(popover as any).hidePopover()
                        }
                    }}
                    className="modal-close-button"
                    aria-label="Close Popover"
                >
                    &times;
                </button>
            </div>

            <div className="stat-picker-overall-wrapper">
                <div className="stat-picker-left-side-wrapper">
                    <div className="stat-picker-button-wrapper">
                        <button
                            type="button"
                            className="btn btn-default stat-picker-default-button"
                            onClick={() =>
                                handleSelectDefaults(DEFAULT_BATTING_STATS)
                            }
                        >
                            Batting Defaults
                        </button>
                        <button
                            type="button"
                            className="btn btn-default stat-picker-default-button"
                            onClick={() =>
                                handleSelectDefaults(DEFAULT_PITCHING_STATS)
                            }
                        >
                            Pitching Defaults
                        </button>
                        <button
                            type="button"
                            className="btn btn-default stat-picker-default-button"
                            onClick={() =>
                                handleSelectDefaults(DEFAULT_CATCHING_STATS)
                            }
                        >
                            Catching Defaults
                        </button>
                    </div>
                    <input
                        className="form-control comp-tool-search"
                        id="stat-picker-search-bar"
                        type="text"
                        placeholder="Search by stat name"
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                    />

                    <div className="stat-picker-scroll-menu">
                        {Object.keys(groupedStats)
                            .filter((key) => {
                                if (currentType === 'batter') {
                                    return [
                                        'batting',
                                        'fielding',
                                        'catching',
                                        'running',
                                        'value',
                                        'pitch_arsenal',
                                    ].includes(key)
                                }
                                return [
                                    'batting',
                                    'pitching',
                                    'value',
                                    'pitch_arsenal',
                                ].includes(key)
                            })
                            .map((groupKey) => {
                                const key = groupKey as StatGroup
                                const statsInGroup = groupedStats[key]

                                // Only render the group if it contains stats matching the filter
                                if (statsInGroup.length === 0) {
                                    return null
                                }

                                let sectionTitle = key as string
                                if (key === 'batting') {
                                    sectionTitle = 'BATTING/PITCHING'
                                } else if (key === 'pitch_arsenal') {
                                    sectionTitle =
                                        currentType === 'batter'
                                            ? 'PITCH ARSENAL (OPP. PITCHERS)'
                                            : 'PITCH ARSENAL'
                                }

                                return (
                                    <StatPickerGroup
                                        key={key}
                                        keyName={key}
                                        sectionTitle={sectionTitle}
                                        statsInGroup={statsInGroup}
                                        expandedPitches={expandedPitches}
                                        searchFilter={searchFilter}
                                        selectedPlayers={selectedPlayers}
                                        togglePitchDrawer={togglePitchDrawer}
                                        renderStatNode={renderStatNode}
                                    />
                                )
                            })}
                    </div>
                </div>

                <StatPickerDraggable
                    draftSelected={draftSelected}
                    setDraftSelected={setDraftSelected}
                    existingDraftTypes={existingDraftTypes}
                    currentType={currentType}
                    handleToggleStat={handleToggleStat}
                />
            </div>

            {isFull ? (
                <div
                    style={{
                        textAlign: 'center',
                        color: 'red',
                        marginTop: '4px',
                        fontSize: '12px',
                    }}
                >
                    <text>
                        Your stats menu is full. To add other stats, deselect
                        one or more selected items.
                    </text>
                </div>
            ) : null}

            <div
                style={{
                    marginTop: isFull ? '4px' : '15px',
                    textAlign: 'center',
                }}
            >
                <button
                    type="button"
                    className="btn btn-default"
                    style={{ padding: '5px', marginRight: '0.5rem' }}
                    onClick={() => setDraftSelected([])}
                >
                    <span>Clear Stats</span>
                </button>
                <button
                    type="button"
                    className="btn btn-default"
                    style={{ padding: '5px' }}
                    onClick={() => {
                        statRef.current?.hidePopover()
                        onAdd(draftSelected as string[])
                    }}
                >
                    Apply Changes
                </button>
            </div>
        </div>
    )
}
